'use client'

import { useState } from 'react'
import { useAuth } from '@/context/AuthContext'
import { subscribePush, isPushSupported } from '@/lib/pushNotifications'
import { saveUserProfile } from '@/lib/userProfile'
import { resendConfirmation } from '@/lib/supabase'

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  )
}

export default function AuthModal({ onClose, initialMode = 'signin' }) {
  const { signIn, signUp, signInWithGoogle } = useAuth()
  const [mode, setMode]         = useState(initialMode) // 'signin' | 'signup'
  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [error, setError]       = useState('')
  const [loading, setLoading]   = useState(false)
  const [resendEmail, setResendEmail] = useState('')
  const [resendSent, setResendSent]   = useState(false)
  const [pushStep, setPushStep] = useState(false) // false | true
  const [digestOptIn, setDigestOptIn] = useState(false)
  const [nameStep, setNameStep]           = useState(false)
  const [displayNameValue, setDisplayNameValue] = useState('')
  const [signedUpUser, setSignedUpUser]   = useState(null)

  async function handleGoogleSignIn() {
    setError('')
    setLoading(true)
    try {
      await signInWithGoogle()
      window.plausible?.('AuthSignin', { props: { method: 'google' } })
      onClose?.()
    } catch (err) {
      setError(err.message ?? 'Google sign-in mislukt. Probeer opnieuw.')
    } finally {
      setLoading(false)
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (mode === 'signup') {
        const newUser = await signUp(email, password)
        window.plausible?.('AuthSignup')
        if (digestOptIn) {
          fetch('/api/subscribe/toggle', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, subscribe: true, frequency: 'daily' }),
          }).catch(() => {})
        }
        setSignedUpUser(newUser)
        setNameStep(true)
        return
      } else {
        await signIn(email, password)
        window.plausible?.('AuthSignin', { props: { method: 'email' } })
      }
      onClose?.()
    } catch (err) {
      const msg = err.message ?? 'Authentication failed. Please try again.'
      setError(msg)
      if (msg.toLowerCase().includes('not confirmed')) setResendEmail(email)
    } finally {
      setLoading(false)
    }
  }

  async function handleResend() {
    if (!resendEmail) return
    setLoading(true)
    try {
      await resendConfirmation(resendEmail)
      setResendSent(true)
      setError('')
    } catch {
      setError('Kon bevestigingsmail niet versturen. Probeer opnieuw.')
    } finally {
      setLoading(false)
    }
  }

  async function handleNameSubmit(e) {
    e.preventDefault()
    if (!displayNameValue.trim()) return
    setLoading(true)
    try {
      if (signedUpUser?.uid) {
        await saveUserProfile(signedUpUser.uid, { displayName: displayNameValue.trim() })
      }
    } catch {}
    setLoading(false)
    if (isPushSupported()) { setPushStep(true); return }
    onClose?.()
  }

  async function handleEnablePush() {
    try {
      await subscribePush()
      window.plausible?.('PushSubscribe')
      window.plausible?.('PushNotificationOptIn')
    } catch {}
    onClose?.()
  }

  if (nameStep) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-8 relative">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Welkom! Hoe mogen we je noemen?</h2>
          <p className="text-sm text-gray-500 mb-6">Je naam wordt weergegeven in je profiel.</p>
          <form onSubmit={handleNameSubmit} className="space-y-4">
            <input
              type="text"
              required
              value={displayNameValue}
              onChange={(e) => setDisplayNameValue(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Voornaam of bijnaam"
              autoFocus
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-medium rounded-lg py-2 text-sm transition-colors"
            >
              {loading ? 'Even geduld…' : 'Doorgaan'}
            </button>
          </form>
        </div>
      </div>
    )
  }

  if (pushStep) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-8 relative text-center">
          <div className="text-4xl mb-4">🔔</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Get instant alerts on new deals</h2>
          <p className="text-sm text-gray-500 mb-6">Enable push notifications to be first in line when flash sales drop.</p>
          <button
            onClick={handleEnablePush}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg py-2.5 text-sm transition-colors mb-3"
          >
            Enable notifications
          </button>
          <button
            onClick={onClose}
            className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            No thanks
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-8 relative">
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl leading-none"
        >
          ✕
        </button>

        <h2 className="text-xl font-semibold mb-6 text-gray-800">
          {mode === 'signin' ? 'Sign in' : 'Create account'}
        </h2>

        {/* Google sign-in button */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="w-full flex items-center justify-center gap-3 border border-gray-300 rounded-lg py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 transition-colors"
        >
          <GoogleIcon />
          Inloggen met Google
        </button>

        {/* Divider */}
        <div className="relative my-5">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-3 text-xs text-gray-400">of ga verder met email</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="••••••••"
            />
          </div>

          {mode === 'signup' && (
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={digestOptIn}
                onChange={(e) => setDigestOptIn(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">Yes, send me weekly flight deals</span>
            </label>
          )}

          {resendSent && (
            <p role="status" className="text-sm text-green-700 bg-green-50 rounded-lg px-3 py-2">
              Bevestigingsmail opnieuw verstuurd — check je inbox (en spam).
            </p>
          )}
          {error && (
            <div role="alert" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
              <p>{error}</p>
              {resendEmail && !resendSent && (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={loading}
                  className="mt-2 underline text-red-700 hover:text-red-900 font-medium"
                >
                  Bevestigingsmail opnieuw versturen →
                </button>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-medium rounded-lg py-2 text-sm transition-colors"
          >
            {loading ? 'Please wait…' : mode === 'signin' ? 'Sign in' : 'Create account'}
          </button>
        </form>

        <p className="mt-5 text-center text-sm text-gray-500">
          {mode === 'signin' ? (
            <>
              No account?{' '}
              <button
                onClick={() => { setMode('signup'); setError('') }}
                className="text-blue-600 hover:underline font-medium"
              >
                Sign up
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button
                onClick={() => { setMode('signin'); setError('') }}
                className="text-blue-600 hover:underline font-medium"
              >
                Sign in
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  )
}
